import { VideoIcon } from '@room-match/shared-ui-components';
import UploadingItem from 'pages/Profiles/EditProfile/Tabs/ImagesVideoAudio/Items/UploadingItem';
import AddModal from 'pages/Profiles/EditProfile/Tabs/ImagesVideoAudio/Modals/AddModal';
import React, { useState } from 'react';
import { useQueryClient } from 'react-query';
import CloseIcon from 'shared/components/Icon/CloseIcon';
import { CustomTypography } from 'shared/elements/CustomTypography';
import StyledDialog from 'shared/elements/StyledDialog/StyledDialog';
import useWindowDimensions from 'shared/hooks/useWindowDimenstion';
import IMedia from 'shared/interfaces/IMedia';
import { IProfileMediaSetSelectPayload } from 'shared/interfaces/IProfile';
import { v4 as uuidv4 } from 'uuid';
import CommonHeader, { HeaderButton } from '../CommonHeader';
import { BorderedCont, Content, NoMediaCont, VideoContentContainer } from '../Dashboard/Dashboard.style';
import VideoItem from '../Items/VideoItem';
import { ButtonCont, DeleteButton, DeleteCont, ModalButton, ModalCont } from '../Media.styles';
import { FileType } from 'shared/enums/FileType';

interface Props {
  data: IMedia[];
  deleteMedia: (ids: string[], type?: FileType) => void;
}

interface IOutputE {
  file: File;
  id: string;
}

const Videos: React.FC<Props> = ({ data: videos, deleteMedia }) => {
  const [show, setshow] = useState(false);
  const [all, setAll] = useState(false);
  const [selectedId, setselectedId] = useState<string[]>([]);
  const { width } = useWindowDimensions();
  const ICON_SIZE = width < 960 ? 30 : 40;
  const [addModal, setAddModal] = useState(false);
  const [toUpload, setToUpload] = useState<IOutputE[]>([]);
  const queryClient = useQueryClient();

  const handleSelect = (media: IMedia) => {
    if (selectedId.indexOf(media.id) > -1) {
      setselectedId((ids) => ids.filter((id) => id !== media.id));
    } else {
      setselectedId((ids) => [...ids, media.id]);
    }
  };

  const handleDelete = () => {
    if (all) {
      const toDelete = videos.map((i) => i.id);
      deleteMedia(toDelete, FileType.VIDEO);
    } else {
      deleteMedia(selectedId, FileType.VIDEO);
    }
    setshow(false);
    setselectedId([]);
  };

  const showWarning = (all: boolean) => {
    setAll(all);
    setshow(true);
  };

  const ContentAction = () => {
    return <HeaderButton onClick={() => setAddModal(true)}> UPLOAD</HeaderButton>;
  };

  const handleOutput = (files: File[]) => {
    const mVid = files.map((i) => ({ file: i, id: uuidv4() }));
    setToUpload(mVid);
    console.log(mVid);
  };

  return (
    <BorderedCont>
      <CommonHeader
        icon={<VideoIcon style={{ width: ICON_SIZE, height: ICON_SIZE, alignSelf: 'center' }} />}
        title="Videos"
        subtitle={`${videos.length} Total Files`}
        contentAction={<ContentAction />}
      />
      <DeleteCont>
        <p style={{ fontSize: width < 960 ? 15 : 18 }}>
          {selectedId.length ? `${selectedId.length} Media ` : 'No Media '}
          Selected
        </p>
        {selectedId.length ? (
          <DeleteButton style={{ marginLeft: width < 960 ? 'auto' : 20 }} onClick={() => showWarning(false)}>
            DELETE SELECTED
          </DeleteButton>
        ) : (
          ''
        )}
      </DeleteCont>
      <VideoContentContainer>
        {videos?.length ? (
          videos.map((m) => {
            if (m.fileType === 'webp') {
              // Handle webp videos differently, e.g., display an error message.
              return (
                <div key={m.id}>
                  <p>Error: Unsupported video format (webp).</p>
                </div>
              );
            } else {
              // Render other video types as usual.
              return (
                <VideoItem key={m.id} item={m} handleSelect={handleSelect} checked={selectedId.indexOf(m.id) > -1} />
              );
            }
          })
        ) : (
          <NoMediaCont>
            <p style={{ marginTop: 50, fontSize: 16 }}>No Media Available</p>
          </NoMediaCont>
        )}
        {toUpload.map((data) => (
          <UploadingItem
            key={data.id}
            type="video"
            data={data.file}
            id={data.id}
            bindToProfile={async (payload: IProfileMediaSetSelectPayload, id: string) => {
              queryClient.invalidateQueries(['profile_media', { file_type: 'video' }]);
              await queryClient.invalidateQueries(['media'], {
                refetchInactive: true,
              });
              setToUpload((prev) => prev.filter((f) => f.id !== id));
            }}
          />
        ))}
      </VideoContentContainer>
      <StyledDialog
        style={{ zIndex: 9999 }}
        open={addModal}
        onClose={() => setAddModal(false)}
        maxWidth="md"
        aria-labelledby="simple-dialog-title"
      >
        <AddModal close={() => setAddModal(false)} type="Videos" output={handleOutput} />
      </StyledDialog>

      <StyledDialog
        style={{ zIndex: 9999 }}
        open={show}
        onClose={() => setshow(false)}
        maxWidth={false}
        aria-labelledby="simple-dialog-title"
      >
        <ModalCont>
          <CloseIcon
            style={{ width: 14, position: 'absolute', cursor: 'pointer', right: 15, top: 10 }}
            onClick={() => setshow(false)}
          />
          <CustomTypography fontSize={25}>
            Delete {all ? (videos.length > 1 ? 'Videos' : 'Video') : selectedId.length > 1 ? 'Videos' : 'Video'}
          </CustomTypography>
          <CustomTypography fontSize={17}>
            Are you sure you want to delete {all ? videos.length : selectedId.length}{' '}
            {all ? (videos.length > 1 ? 'videos' : 'video') : selectedId.length > 1 ? 'videos' : 'video'}?
          </CustomTypography>
          <p style={{ marginTop: 33, textAlign: 'center', wordBreak: 'break-word', fontSize: 16 }}>
            <span style={{ color: '#ed3030' }}>
              {' '}
              This will remove{' '}
              {all
                ? videos.length > 1
                  ? 'these videos'
                  : 'this video'
                : selectedId.length > 1
                  ? 'these videos'
                  : 'this video'}
            </span>{' '}
            from your media manager as well as any profile it is currently associated with, this cannot be undone.
          </p>
          <ButtonCont>
            <ModalButton style={{ backgroundColor: '#ed3030', color: '#fff' }} onClick={handleDelete}>
              Yes, delete {all ? (videos.length > 1 ? 'videos' : 'video') : selectedId.length > 1 ? 'videos' : 'video'}
            </ModalButton>
            <ModalButton
              style={{ border: '1px solid #3e8bff', marginTop: 15, backgroundColor: 'transparent' }}
              onClick={() => setshow(false)}
            >
              Cancel
            </ModalButton>
          </ButtonCont>
        </ModalCont>
      </StyledDialog>
    </BorderedCont>
  );
};

export default Videos;
